import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, StatusBadge, TableRowActions, SortableTh, TableFilterBar, TablePagination } from '@/shared/components'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { useStructures, useDeleteStructure } from '../hooks/use-structures'
import { useTableSort } from '@/shared/hooks/use-table-sort'
import { usePagination } from '@/shared/hooks/use-pagination'
import { cn } from '@/lib/utils'

const TYPE_LABELS: Record<string, string> = {
  ECOLE: 'École',
  COLLEGE: 'Collège',
  LYCEE: 'Lycée',
  ASSOCIATION: 'Association',
  AUTRE: 'Autre',
}

export function StructureList() {
  const { t } = useTranslation()
  const { data: structures, isLoading } = useStructures()
  const deleteStructure = useDeleteStructure()
  const [statusFilter, setStatusFilter] = useState('all')
  const [deleteDialog, setDeleteDialog] = useState<{ id: number; label: string } | null>(null)

  const filtered = useMemo(() => {
    if (!structures) return []
    if (statusFilter === 'actif') return structures.filter((s) => s.actif)
    if (statusFilter === 'inactif') return structures.filter((s) => !s.actif)
    return structures
  }, [structures, statusFilter])

  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(filtered, 'nom')
  const { paginatedData, currentPage, totalPages, totalItems, pageSize, setCurrentPage } = usePagination(sortedData)

  const handleDelete = (id: number, nom: string) => {
    setDeleteDialog({ id, label: nom })
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteStructure.mutate(deleteDialog.id)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!structures || structures.length === 0) {
    return <EmptyState message="Aucune structure disponible. Créez votre première structure !" />
  }

  const counts = {
    all: structures.length,
    actif: structures.filter((s) => s.actif).length,
    inactif: structures.filter((s) => !s.actif).length,
  }

  return (
    <>
      <TableFilterBar
        filters={[{
          key: 'status',
          label: 'Statut',
          value: statusFilter,
          options: [
            { value: 'all', label: 'Tous', count: counts.all },
            { value: 'actif', label: 'Actifs', count: counts.actif },
            { value: 'inactif', label: 'Inactifs', count: counts.inactif },
          ],
        }]}
        onFilterChange={(_key, value) => setStatusFilter(value)}
      />
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
              <SortableTh label="Nom" sortKey="nom" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Type" sortKey="type" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Ville" sortKey="ville" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Statut" sortKey="actif" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {paginatedData.map((structure) => (
              <tr key={structure.id} className={cn('transition-colors hover:bg-neutral-50', !structure.actif && 'opacity-50')}>
                <td className="px-4 py-3 text-sm font-medium text-neutral-900">{structure.nom}</td>
                <td className="px-4 py-3">
                  <StatusBadge label={TYPE_LABELS[structure.type] || structure.type} variant="info" />
                </td>
                <td className="px-4 py-3 text-sm text-neutral-500">{structure.ville || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {structure.actif ? <StatusBadge label="Actif" variant="active" /> : <StatusBadge label="Inactif" variant="inactive" />}
                    {structure.estSysteme && <StatusBadge label="Système" variant="system" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <TableRowActions
                    editTo={`/administration/structures/${structure.id}`}
                    onDelete={() => handleDelete(structure.id, structure.nom)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={`Supprimer la structure "${deleteDialog?.label}" ?`}
        description="Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </>
  )
}
